"use client";
import Link from "next/link";
import { HeaderItem } from "@/types/menu";
import { usePathname } from "next/navigation";
import { Icon } from "@iconify/react";
import { useState, useRef, useEffect } from "react";

interface HeaderLinkProps {
  item: HeaderItem;
  depthLevel?: number;
}

const HeaderLink: React.FC<HeaderLinkProps> = ({ item, depthLevel = 0 }) => {
  const path = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

const isTopLevel = depthLevel === 0;

const isActive =
  item.href === "/"
    ? path === "/"
    : item.href !== "#" && (path === item.href || path.startsWith(item.href + "/"));
    
  // Helper to determine if we should render a Link or a Button
  const isLink = item.href && item.href !== "#";

  // Force close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [path]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  // Base styling
  const commonClasses = `
    flex items-center gap-1 transition-colors duration-200
    ${
      isActive
        ? "text-primary"
        : "text-midnight_text dark:text-white hover:text-primary dark:hover:text-primary"
    }
    ${
      isTopLevel
        ? "text-[17px] py-4"
        : "text-sm px-4 py-3 justify-between w-full hover:bg-gray-50 dark:hover:bg-white/5"
    }
  `;

  return (
    <div
      className={`relative h-full ${
        !isTopLevel ? "w-full" : "flex items-center"
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {item.submenu ? (
        <>
          {/* --- PARENT ITEM (Link or Button) --- */}
          {isLink ? (
            // SEO OPTIMIZED: Renders an <a> tag
            <Link
              href={item.href}
              className={commonClasses}
              onClick={() => setIsOpen(false)} // Close menu on navigation
            >
              {item.label}
              <Icon
                icon="ion:chevron-down"
                className={`text-xs transition-transform duration-300 ${
                  !isTopLevel
                    ? isOpen
                      ? "rotate-0"
                      : "-rotate-90"
                    : isOpen
                    ? "rotate-180"
                    : "rotate-0"
                }`}
              />
            </Link>
          ) : (
            // Toggle Button (for items like "Products" that are just containers)
            <button type="button" className={commonClasses}>
              {item.label}
              <Icon
                icon="ion:chevron-down"
                className={`text-xs transition-transform duration-300 ${
                  !isTopLevel
                    ? isOpen
                      ? "rotate-0"
                      : "-rotate-90"
                    : isOpen
                    ? "rotate-180"
                    : "rotate-0"
                }`}
              />
            </button>
          )}

          {/* --- DROPDOWN MENU --- */}
          <div
            className={`
              absolute z-50
              min-w-[240px] bg-white dark:bg-darkheader 
              border border-gray-100 dark:border-white/5 shadow-xl rounded-xl
              origin-top-left

              ${isOpen
                ? "opacity-100 scale-100 translate-y-0 visible pointer-events-auto transition-all duration-150 ease-out"
                : "opacity-0 scale-95 translate-y-1 invisible pointer-events-none transition-none"
              }

              ${isTopLevel ? "top-[90%] left-0" : "top-0 left-full ml-1"}
            `}
          >
            {/* Invisible bridge for nested flyouts */}
            {!isTopLevel && (
              <div className="absolute right-full top-0 w-2 h-full bg-transparent block" />
            )}

            <div className="py-2 flex flex-col">
              {item.submenu.map((subItem, index) => (
                <HeaderLink
                  key={index}
                  item={subItem}
                  depthLevel={depthLevel + 1}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        // --- STANDARD LINK ---
        <Link
          href={item.href}
          className={commonClasses}
          onClick={() => setIsOpen(false)}
        >
          {item.label}
        </Link>
      )}
    </div>
  );
};

export default HeaderLink;
